import React, { useState, useEffect, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { formatCurrency } from '../../utils/formatters';
import { generatePaymentPDF } from '../../utils/pdfGenerator';
import ErrorModal from './ErrorModal';
import { getCurrentArgentinaISOString, formatArgentinaDate } from '../../utils/dateUtils';
import { getNextReceiptNumber } from '../../utils/numberGenerator';
import { supabase } from '../../lib/supabase';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  entityName: string;
  entityType: 'client' | 'supplier';
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  entityId,
  entityName,
  entityType,
}) => {
  const { invoices, payments, addPayment } = useAppStore();
  const [isAccountPayment, setIsAccountPayment] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getCurrentArgentinaISOString());
  const [method, setMethod] = useState<'cash' | 'transfer' | 'card' | 'check' | 'mercadopago'>('cash');
  const [reference, setReference] = useState('');
  const [currency, setCurrency] = useState<'ARS' | 'USD'>('ARS');
  const [exchangeRate, setExchangeRate] = useState('1480');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState<any[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDate(getCurrentArgentinaISOString());
      setAmount('');
      setSelectedInvoice('');
      setIsAccountPayment(true);
      setMethod('cash');
      setReference('');
      setCurrency('ARS');
      setExchangeRate('1480');
      setShowAdvanced(false);
      fetchPendingInvoices();
    }
  }, [isOpen, entityId]);

  const fetchPendingInvoices = async () => {
    setPendingLoading(true);
    try {
      const { data } = await supabase
        .from('invoices')
        .select('*')
        .eq('entity_id', entityId)
        .gt('balance', 0)
        .eq('voided', false)
        .order('date', { ascending: false });
      setPendingInvoices(data || []);
      if (data?.length === 1) {
        setSelectedInvoice(data[0].id);
        const paid = payments.filter(p => p.invoice_id === data[0].id && !p.voided).reduce((s, p) => s + p.amount, 0);
        setAmount(Math.max(0, data[0].total - paid).toString());
        const inv = data[0] as any;
        if (inv.currency === 'USD') setCurrency('USD');
        if (inv.exchange_rate) setExchangeRate(String(inv.exchange_rate));
      }
    } catch {
      setPendingInvoices([]);
    } finally {
      setPendingLoading(false);
    }
  };

  const selectedInv = useMemo(() => pendingInvoices.find(i => i.id === selectedInvoice), [pendingInvoices, selectedInvoice]);
  const balance = useMemo(() => {
    if (!selectedInv) return 0;
    const paid = payments.filter(p => p.invoice_id === selectedInv.id && !p.voided).reduce((s, p) => s + p.amount, 0);
    return Math.max(0, selectedInv.total - paid);
  }, [selectedInv, payments]);

  const handleInvoiceChange = (id: string) => {
    setSelectedInvoice(id);
    const inv = pendingInvoices.find(i => i.id === id);
    if (inv) {
      const paid = payments.filter(p => p.invoice_id === inv.id && !p.voided).reduce((s, p) => s + p.amount, 0);
      setAmount(Math.max(0, inv.total - paid).toString());
      const i = inv as any;
      if (i.currency === 'USD') setCurrency('USD');
      else setCurrency('ARS');
      if (i.exchange_rate) setExchangeRate(String(i.exchange_rate));
    } else setAmount('');
  };

  const invoiceOptions = useMemo(() => {
    if (pendingLoading) return [{ value: '', label: 'Cargando...' }];
    if (pendingInvoices.length === 0) return [{ value: '', label: 'Sin facturas pendientes' }];
    return [
      { value: '', label: 'Seleccionar...' },
      ...pendingInvoices.map(inv => {
        const paid = payments.filter(p => p.invoice_id === inv.id && !p.voided).reduce((s, p) => s + p.amount, 0);
        const bal = Math.max(0, inv.total - paid);
        return { value: inv.id, label: `${inv.number} (${formatCurrency(bal)})` };
      }),
    ];
  }, [pendingInvoices, payments, pendingLoading]);

  const setPct = (pct: number) => {
    if (selectedInv && balance > 0) setAmount((balance * pct / 100).toFixed(2));
  };

  const invoiceCurrency = (selectedInv as any)?.currency || 'ARS';
  const needsTC = currency !== invoiceCurrency;
  const rate = parseFloat(exchangeRate) || 0;
  const numAmount = parseFloat(amount) || 0;
  const amountARS = currency === 'USD' ? numAmount * rate : numAmount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || numAmount <= 0) {
      setError('Ingresá un monto válido');
      setShowErrorModal(true);
      return;
    }
    if (!isAccountPayment && !selectedInvoice) {
      setError('Seleccioná una factura');
      setShowErrorModal(true);
      return;
    }
    setIsLoading(true);
    try {
      const receiptNumber = await getNextReceiptNumber();
      const payment: any = {
        date,
        invoice_id: isAccountPayment ? null : selectedInvoice,
        amount: numAmount,
        method,
        reference: isAccountPayment ? `${entityType}:${entityId}` : reference.trim() || null,
        invoice_type: isAccountPayment ? null : undefined,
        currency,
        exchange_rate: currency === 'USD' ? Math.max(0.000001, rate) : 1,
        amount_ars: amountARS,
      };
      await addPayment(payment);
      if (!isAccountPayment && selectedInv?.entity_type === 'client') {
        await generatePaymentPDF({ ...payment, id: `temp-${Date.now()}`, receipt_number: receiptNumber }, entityName);
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al registrar');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-gray-900 shadow-xl overflow-hidden focus:outline-none">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Registrar pago</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{entityName}</p>
                </div>
                <Dialog.Close asChild>
                  <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Cerrar">
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg mb-4">
                <button type="button" onClick={() => { setIsAccountPayment(true); setSelectedInvoice(''); setAmount(''); }}
                  className={`flex-1 py-2 rounded-md text-sm font-medium ${isAccountPayment ? 'bg-white dark:bg-gray-700 shadow-sm text-indigo-600' : 'text-gray-600'}`}>
                  A cuenta
                </button>
                <button type="button" onClick={() => setIsAccountPayment(false)}
                  className={`flex-1 py-2 rounded-md text-sm font-medium ${!isAccountPayment ? 'bg-white dark:bg-gray-700 shadow-sm text-indigo-600' : 'text-gray-600'}`}>
                  A factura
                </button>
              </div>

              {!isAccountPayment && (
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Factura</label>
                  <select value={selectedInvoice} onChange={(e) => handleInvoiceChange(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                    disabled={pendingLoading}>
                    {invoiceOptions.map(o => (
                      <option key={o.value || 'opt'} value={o.value} disabled={o.value === '' && invoiceOptions.length > 1}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Fecha</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Monto *</label>
                  <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="0" min="0.01" step="0.01" required
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-right"
                    disabled={!isAccountPayment && pendingInvoices.length === 0} />
                  {!isAccountPayment && selectedInvoice && balance > 0 && (
                    <div className="flex gap-1 mt-1">
                      {[25, 50, 75, 100].map(p => (
                        <button key={p} type="button" onClick={() => setPct(p)} className="px-2 py-0.5 text-[10px] rounded bg-gray-100 dark:bg-gray-700 hover:bg-indigo-100">
                          {p}%
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Método</label>
                <select value={method} onChange={(e) => setMethod(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm">
                  <option value="cash">Efectivo</option>
                  <option value="transfer">Transferencia</option>
                  <option value="card">Tarjeta</option>
                  <option value="check">Cheque</option>
                  <option value="mercadopago">MercadoPago</option>
                </select>
              </div>

              <div className="mb-4">
                <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                  {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Más opciones
                </button>
                {showAdvanced && (
                  <div className="mt-2 p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                    <Select label="Moneda" value={currency} onChange={(v) => setCurrency(v as 'ARS' | 'USD')} options={[{ value: 'ARS', label: 'ARS' }, { value: 'USD', label: 'USD' }]} />
                    {needsTC && <Input label="Tipo de cambio" type="number" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} min="0" step="0.01" />}
                    {method !== 'cash' && <Input label="Referencia" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Nº operación" />}
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                <Button type="submit" disabled={!amount || numAmount <= 0 || isLoading || (!isAccountPayment && !selectedInvoice)} className="flex-1">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registrar'}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <ErrorModal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} message={error || 'Error'} />
    </>
  );
};

export default PaymentModal;
